import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getTeams, createTeam, updateTeam, deleteTeam, addTeamMember, removeTeamMember } from './coachApi';

const SPORT_ICONS = ['⚽', '🏀', '🏈', '🎾', '🏊', '🚴', '🏋️', '🤸', '🏐', '🥊', '🏒', '⛷️', '🏃', '🤺', '🎯'];
const TIER_TEAM_LIMITS = { club: 5, pro: Infinity, elite: Infinity };

export default function CoachTeamsTab({ coachId, tier, clients, lang }) {
  const nl = lang === 'nl';
  const limit = TIER_TEAM_LIMITS[tier] || 5;

  const [teams, setTeams]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTeam, setActiveTeam] = useState(null);
  const [showNew, setShowNew]     = useState(false);
  const [form, setForm]           = useState({ name: '', sport: '⚽', color: '#5B9ED0', description: '' });
  const [saving, setSaving]       = useState(false);

  const load = useCallback(() => {
    getTeams(coachId).then(d => { if (Array.isArray(d)) setTeams(d); setLoading(false); }).catch(() => setLoading(false));
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await createTeam(coachId, { ...form, coach_id: coachId }).catch(() => {});
    setSaving(false);
    setShowNew(false);
    setForm({ name: '', sport: '⚽', color: '#5B9ED0', description: '' });
    load();
  };

  const handleDelete = async (tid) => {
    if (!window.confirm(nl ? 'Team verwijderen?' : 'Delete team?')) return;
    await deleteTeam(coachId, tid).catch(() => {});
    if (activeTeam?.id === tid) setActiveTeam(null);
    load();
  };

  const handleAddMember = async (uid) => {
    if (!activeTeam) return;
    await addTeamMember(coachId, activeTeam.id, uid).catch(() => {});
    const updated = { ...activeTeam, members: [...(activeTeam.members || []), uid] };
    setActiveTeam(updated);
    setTeams(ts => ts.map(t => t.id === activeTeam.id ? updated : t));
  };

  const handleRemoveMember = async (uid) => {
    if (!activeTeam) return;
    await removeTeamMember(coachId, activeTeam.id, uid).catch(() => {});
    const updated = { ...activeTeam, members: (activeTeam.members || []).filter(m => m !== uid) };
    setActiveTeam(updated);
    setTeams(ts => ts.map(t => t.id === activeTeam.id ? updated : t));
  };

  // ── Team detail ──────────────────────────────────────────────────────────────
  if (activeTeam) {
    const members  = activeTeam.members || [];
    const inTeam   = (clients || []).filter(c => members.includes(c.user_id));
    const notInTeam = (clients || []).filter(c => !members.includes(c.user_id));
    return (
      <div style={{ padding: '0 0 32px' }}>
        <button onClick={() => setActiveTeam(null)} style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 16px' }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: `${activeTeam.color || _T.accent}20`, border: `2px solid ${activeTeam.color || _T.accent}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
            {activeTeam.sport || '⚽'}
          </div>
          <div>
            <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{activeTeam.name}</div>
            {activeTeam.description && <div style={{ color: C.silver, fontSize: 12 }}>{activeTeam.description}</div>}
            <div style={{ color: C.silver2, fontSize: 11, marginTop: 2 }}>{members.length} {nl ? 'leden' : 'members'}</div>
          </div>
        </div>

        {/* Team wellness summary */}
        {inTeam.length > 0 && (
          <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'TEAM WELLNESS' : 'TEAM WELLNESS'}</div>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#5B9ED0', fontSize: 18, fontWeight: 700 }}>
                  {inTeam.filter(c => c.avg_sleep).length > 0
                    ? (inTeam.reduce((s, c) => s + (c.avg_sleep || 0), 0) / inTeam.filter(c => c.avg_sleep).length).toFixed(1) + 'h'
                    : '—'}
                </div>
                <div style={{ color: C.silver2, fontSize: 10 }}>{nl ? 'Slaap' : 'Sleep'}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#2A9E82', fontSize: 18, fontWeight: 700 }}>
                  {inTeam.filter(c => c.avg_mood).length > 0
                    ? (inTeam.reduce((s, c) => s + (c.avg_mood || 0), 0) / inTeam.filter(c => c.avg_mood).length).toFixed(1) + '/5'
                    : '—'}
                </div>
                <div style={{ color: C.silver2, fontSize: 10 }}>{nl ? 'Stemming' : 'Mood'}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: _T.accent, fontSize: 18, fontWeight: 700 }}>{members.length}</div>
                <div style={{ color: C.silver2, fontSize: 10 }}>{nl ? 'Atleten' : 'Athletes'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Current members */}
        <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 10 }}>{nl ? 'TEAMLEDEN' : 'TEAM MEMBERS'}</div>
        {inTeam.length === 0 ? (
          <div style={{ color: C.silver2, fontSize: 13, marginBottom: 14 }}>{nl ? 'Nog geen leden' : 'No members yet'}</div>
        ) : (
          inTeam.map(c => (
            <div key={c.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{c.avatar || '👤'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                <div style={{ color: C.silver2, fontSize: 11 }}>{c.avg_sleep ? `😴 ${c.avg_sleep}h` : ''}{c.avg_mood ? `  💚 ${c.avg_mood}/5` : ''}</div>
              </div>
              <button onClick={() => handleRemoveMember(c.user_id)}
                style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>
          ))
        )}

        {/* Add from non-members */}
        {notInTeam.length > 0 && (
          <>
            <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginTop: 16, marginBottom: 10 }}>{nl ? 'TOEVOEGEN' : 'ADD MEMBERS'}</div>
            {notInTeam.map(c => (
              <div key={c.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${C.silver}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, opacity: 0.6 }}>{c.avatar || '👤'}</div>
                <div style={{ flex: 1, color: C.silver, fontSize: 13 }}>{c.name}</div>
                <button onClick={() => handleAddMember(c.user_id)}
                  style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 8, color: _T.accent, fontSize: 12, fontWeight: 700, padding: '5px 12px', cursor: 'pointer' }}>
                  +
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 700 }}>{nl ? 'Teams & Groepen' : 'Teams & Groups'}</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>
            {teams.length}{limit !== Infinity ? `/${limit}` : ''} {nl ? 'teams' : 'teams'}
          </div>
        </div>
        <button onClick={() => setShowNew(v => !v)} disabled={teams.length >= limit}
          style={{ background: teams.length >= limit ? _T.bg4 : `${_T.accent}20`, border: `1px solid ${teams.length >= limit ? _T.cardBorderBot : _T.accent + '55'}`, borderRadius: 10, color: teams.length >= limit ? C.silver2 : _T.accent, fontSize: 13, fontWeight: 600, padding: '8px 14px', cursor: teams.length >= limit ? 'not-allowed' : 'pointer' }}>
          + {nl ? 'Team' : 'Team'}
        </button>
      </div>

      {showNew && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder={nl ? 'Teamnaam *' : 'Team name *'}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder={nl ? 'Omschrijving (optioneel)' : 'Description (optional)'}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />
          <div style={{ fontSize: 11, color: C.silver, marginBottom: 6 }}>{nl ? 'Sport / icoon' : 'Sport / icon'}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {SPORT_ICONS.map(ic => (
              <button key={ic} onClick={() => setForm(f => ({ ...f, sport: ic }))}
                style={{ width: 36, height: 36, borderRadius: 8, border: `2px solid ${form.sport === ic ? _T.accent : _T.cardBorderBot}`, background: form.sport === ic ? `${_T.accent}20` : _T.bg4, fontSize: 18, cursor: 'pointer' }}>
                {ic}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.silver, marginBottom: 6 }}>{nl ? 'Teamkleur' : 'Team color'}</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {['#5B9ED0', '#4AC87A', '#D4873A', '#C8A84B', '#7B6FD0', '#C05050', '#2A9E82'].map(col => (
              <button key={col} onClick={() => setForm(f => ({ ...f, color: col }))}
                style={{ width: 28, height: 28, borderRadius: '50%', background: col, border: `3px solid ${form.color === col ? 'white' : 'transparent'}`, cursor: 'pointer' }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowNew(false)} style={{ flex: 1, background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
              {nl ? 'Annuleren' : 'Cancel'}
            </button>
            <button onClick={handleCreate} disabled={saving || !form.name.trim()}
              style={{ flex: 1, background: form.name.trim() ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: 'none', borderRadius: 10, color: form.name.trim() ? '#0a1220' : C.silver2, fontWeight: 700, fontSize: 13, padding: '10px', cursor: form.name.trim() ? 'pointer' : 'not-allowed' }}>
              {saving ? '...' : (nl ? 'Aanmaken' : 'Create')}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>...</div>
      ) : teams.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🏆</div>
          <div style={{ fontSize: 14 }}>{nl ? 'Nog geen teams' : 'No teams yet'}</div>
        </div>
      ) : (
        teams.map(t => {
          const memberCount = t.members?.length || 0;
          return (
            <div key={t.id} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: `${t.color || _T.accent}20`, border: `2px solid ${t.color || _T.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                  {t.sport || '⚽'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{t.name}</div>
                  <div style={{ color: C.silver, fontSize: 12 }}>{memberCount} {nl ? 'leden' : 'members'}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setActiveTeam(t)}
                    style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 8, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' }}>
                    {nl ? 'Beheer' : 'Manage'}
                  </button>
                  <button onClick={() => handleDelete(t.id)}
                    style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 18, cursor: 'pointer' }}>×</button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
